"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type MixedMartialArtsDisplayPreviewProps = {
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

/*
 * Inside the octagon on fight night: a pale canvas lit by one hard overhead
 * spot, the chain-link cage wrapping around in perspective with red and blue
 * corner pads on the flanks, and a pair of fight gloves lying in the pool of
 * light (the focal point). The crowd is camera-flash bokeh behind the cage.
 * The near edge of the canvas runs under the participant pill.
 *
 * Motion: the spot's haze breathes — CSS opacity only, off under reduced
 * motion.
 */
const MMA_KEYFRAMES = `
@keyframes mma-haze { 0%, 100% { opacity: 0.7 } 50% { opacity: 1 } }
.mma-haze { animation: mma-haze 5s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) { .mma-haze { animation: none } }
`;

// Stable scatter from an integer LCG — exact on every engine (V8 and JSC
// disagree on trig in the last bits), so SSR and hydration always match.
const CROWD_BOKEH = (() => {
  let seed = 20261014;
  const next = () => {
    seed = (Math.imul(seed, 1103515245) + 12345) >>> 0;
    return (seed >>> 8) / 16777216;
  };
  return Array.from({ length: 34 }, () => {
    const x = Math.round(next() * 1000) / 10;
    const t = next();
    // t^1.5 via sqrt (IEEE-exact everywhere) bunches the crowd up top.
    const y = Math.round(t * Math.sqrt(t) * 1000) / 10;
    const r = Math.round((0.6 + next() * 1.4) * 10) / 10;
    const o = Math.round(((0.75 - y / 160) * next() + 0.1) * 100) / 100;
    return { x, y, r, o, warm: next() > 0.35 };
  });
})();

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `mma-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

type Point = { x: number; y: number };

// A regular octagon on the floor, projected: squashed vertically and
// narrowed toward the back. Fence height scales with nearness.
const V = {
  backLeft: { x: 107, y: 71 },
  backRight: { x: 193, y: 71 },
  rightBack: { x: 261, y: 104 },
  rightFront: { x: 272, y: 152 },
  frontRight: { x: 203, y: 185 },
  frontLeft: { x: 97, y: 185 },
  leftFront: { x: 28, y: 152 },
  leftBack: { x: 39, y: 104 },
} satisfies Record<string, Point>;

type VertexName = keyof typeof V;

const FENCE_HEIGHT: Record<VertexName, number> = {
  backLeft: 36,
  backRight: 36,
  rightBack: 40,
  leftBack: 40,
  rightFront: 44,
  leftFront: 44,
  frontRight: 46,
  frontLeft: 46,
};

const FLOOR_ORDER: VertexName[] = [
  "backLeft",
  "backRight",
  "rightBack",
  "rightFront",
  "frontRight",
  "frontLeft",
  "leftFront",
  "leftBack",
];

// Far panels first so nearer ones paint over them.
const BACK_PANELS: Array<[VertexName, VertexName]> = [
  ["backLeft", "backRight"],
  ["leftBack", "backLeft"],
  ["backRight", "rightBack"],
  ["leftFront", "leftBack"],
  ["rightBack", "rightFront"],
];
const FRONT_PANELS: Array<[VertexName, VertexName]> = [
  ["frontLeft", "leftFront"],
  ["rightFront", "frontRight"],
];

const POSTS: Array<{ name: VertexName; width: number; pad: string }> = [
  { name: "backLeft", width: 3, pad: "#18181c" },
  { name: "backRight", width: 3, pad: "#18181c" },
  { name: "leftBack", width: 3.6, pad: "#18181c" },
  { name: "rightBack", width: 3.6, pad: "#18181c" },
  { name: "leftFront", width: 5, pad: "#d62839" },
  { name: "rightFront", width: 5, pad: "#1f6fe0" },
];

const top = (name: VertexName) => ({
  x: V[name].x,
  y: V[name].y - FENCE_HEIGHT[name],
});

const panelPoints = ([a, b]: [VertexName, VertexName]) =>
  [V[a], V[b], top(b), top(a)].map((p) => `${p.x},${p.y}`).join(" ");

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <radialGradient id={id("canvas")} cx="0.5" cy="0.48" r="0.62">
          <stop offset="0" stopColor="#f7f4ec" />
          <stop offset="0.5" stopColor="#d9d3c6" />
          <stop offset="1" stopColor="#7d776c" />
        </radialGradient>
        <radialGradient id={id("pool")} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={id("beam")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#fff8e6" stopOpacity="0.32" />
          <stop offset="0.7" stopColor="#fff8e6" stopOpacity="0.08" />
          <stop offset="1" stopColor="#fff8e6" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={id("panel")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#0b0b0f" stopOpacity="0.55" />
          <stop offset="1" stopColor="#0b0b0f" stopOpacity="0.15" />
        </linearGradient>
        <pattern
          id={id("mesh")}
          width="5"
          height="5"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M0 0 L5 5 M5 0 L0 5"
            stroke="#c7cfd8"
            strokeOpacity="0.55"
            strokeWidth="0.5"
          />
        </pattern>
        <linearGradient id={id("red")} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#ff5a5f" />
          <stop offset="0.5" stopColor="#d1172b" />
          <stop offset="1" stopColor="#7a0a16" />
        </linearGradient>
        <linearGradient id={id("blue")} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#5aa8ff" />
          <stop offset="0.5" stopColor="#1f63d6" />
          <stop offset="1" stopColor="#0b2f78" />
        </linearGradient>
        <linearGradient id={id("post")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#3a3d45" />
          <stop offset="0.4" stopColor="#8b919c" />
          <stop offset="1" stopColor="#1c1e23" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** One empty fight glove, top view, pointing up; tinted by `fill`. */
function Glove({
  fill,
  strap,
  transform,
}: {
  fill: string;
  strap: string;
  transform: string;
}) {
  return (
    <g transform={transform}>
      {/* Wrist cuff */}
      <rect x="-11" y="8" width="22" height="22" rx="5" fill="#16161a" />
      <rect x="-11" y="14" width="22" height="9" rx="2" fill={strap} />
      <rect
        x="-11"
        y="14"
        width="22"
        height="1.4"
        fill="#ffffff"
        opacity="0.35"
      />
      {/* Thumb */}
      <path d="M-12 -2 C-19 0 -20 10 -14 13 C-11 14 -9 10 -9 6 Z" fill={fill} />
      {/* Knuckle pad */}
      <path
        d="M-13 11 C-15 -6 -12 -20 0 -21 C12 -20 15 -6 13 11 Z"
        fill={fill}
      />
      {/* Open-finger lip at the front */}
      <path
        d="M-9 -18.5 Q0 -22.5 9 -18.5"
        stroke="#16161a"
        strokeOpacity="0.55"
        strokeWidth="1.4"
        fill="none"
      />
      {/* Stitch line + specular */}
      <path
        d="M-10 6 C-11 -5 -9 -15 0 -16.5 C9 -15 11 -5 10 6"
        stroke="#ffffff"
        strokeOpacity="0.22"
        strokeWidth="0.7"
        strokeDasharray="1.5 1.5"
        fill="none"
      />
      <ellipse
        cx="-4"
        cy="-11"
        rx="5"
        ry="3"
        transform="rotate(-20 -4 -11)"
        fill="#ffffff"
        opacity="0.45"
      />
    </g>
  );
}

function Octagon({ id }: { id: IdFn }) {
  const floor = FLOOR_ORDER.map((n) => `${V[n].x},${V[n].y}`).join(" ");
  const rail = (names: VertexName[]) =>
    names
      .map((n, i) => `${i === 0 ? "M" : "L"}${top(n).x} ${top(n).y}`)
      .join(" ");
  return (
    <svg
      className="absolute top-0 left-1/2 -translate-x-1/2"
      width="300"
      height="220"
      viewBox="0 0 300 220"
      aria-hidden="true"
      focusable="false"
    >
      {/* Back cage */}
      {BACK_PANELS.map((panel) => (
        <g key={`panel-${panel[0]}-${panel[1]}`}>
          <polygon points={panelPoints(panel)} fill={`url(#${id("panel")})`} />
          <polygon points={panelPoints(panel)} fill={`url(#${id("mesh")})`} />
        </g>
      ))}
      <path
        d={rail([
          "leftFront",
          "leftBack",
          "backLeft",
          "backRight",
          "rightBack",
          "rightFront",
        ])}
        stroke="#09090c"
        strokeWidth="4"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d={rail([
          "leftFront",
          "leftBack",
          "backLeft",
          "backRight",
          "rightBack",
          "rightFront",
        ])}
        transform="translate(0 -1.6)"
        stroke="#7c828d"
        strokeOpacity="0.7"
        strokeWidth="0.8"
        strokeLinejoin="round"
        fill="none"
      />

      {/* Canvas */}
      <polygon points={floor} fill={`url(#${id("canvas")})`} />
      <polygon
        points={floor}
        stroke="#141418"
        strokeWidth="2.5"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Painted centre logo + sponsor arcs */}
      <polygon
        points={FLOOR_ORDER.map(
          (n) => `${150 + (V[n].x - 150) * 0.3},${128 + (V[n].y - 128) * 0.3}`,
        ).join(" ")}
        stroke="#9f988b"
        strokeWidth="1.4"
        fill="#e9e3d6"
        fillOpacity="0.5"
      />
      <path
        d="M70 112 Q150 86 230 112"
        stroke="#b7b0a3"
        strokeWidth="5"
        strokeOpacity="0.35"
        fill="none"
      />
      <path
        d="M66 160 Q150 182 234 160"
        stroke="#b7b0a3"
        strokeWidth="5"
        strokeOpacity="0.3"
        fill="none"
      />
      <ellipse cx="150" cy="126" rx="96" ry="40" fill={`url(#${id("pool")})`} />

      {/* Gloves in the light */}
      <ellipse cx="121" cy="133" rx="26" ry="9" fill="#1a1712" opacity="0.3" />
      <ellipse cx="181" cy="135" rx="26" ry="9" fill="#1a1712" opacity="0.3" />
      <Glove
        fill={`url(#${id("red")})`}
        strap="#ff7a7f"
        transform="translate(119 121) rotate(-58) scale(1.25 0.95)"
      />
      <Glove
        fill={`url(#${id("blue")})`}
        strap="#7ab8ff"
        transform="translate(182 123) rotate(54) scale(-1.25 0.95)"
      />

      {/* Overhead spot */}
      <polygon
        className="mma-haze"
        points="132,-4 168,-4 246,150 54,150"
        fill={`url(#${id("beam")})`}
      />

      {/* Posts */}
      {POSTS.map(({ name, width, pad }) => {
        const t = top(name);
        const b = V[name];
        return (
          <g key={`post-${name}`}>
            <rect
              x={b.x - width / 2}
              y={t.y - 2}
              width={width}
              height={b.y - t.y + 2}
              rx={width / 2}
              fill={`url(#${id("post")})`}
            />
            <rect
              x={b.x - width / 2 - 1.2}
              y={t.y - 3}
              width={width + 2.4}
              height={(b.y - t.y) * 0.62}
              rx={(width + 2.4) / 2}
              fill={pad}
            />
            <rect
              x={b.x - width / 2 - 0.4}
              y={t.y - 1.5}
              width={1}
              height={(b.y - t.y) * 0.5}
              rx={0.5}
              fill="#ffffff"
              opacity="0.35"
            />
          </g>
        );
      })}

      {/* Near cage, see-through, framing the flanks */}
      {FRONT_PANELS.map((panel) => (
        <polygon
          key={`front-${panel[0]}-${panel[1]}`}
          points={panelPoints(panel)}
          fill={`url(#${id("mesh")})`}
          opacity="0.5"
        />
      ))}
    </svg>
  );
}

export function MixedMartialArtsDisplayPreview({
  participants,
}: MixedMartialArtsDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="MMA"
      backgroundClassName="bg-[#060609]"
      participants={participants}
      scene={
        <>
          <style href="mma-scene-keyframes" precedence="default">
            {MMA_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          {/* Arena: rig glare overhead, red and blue corner wash */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(60% 55% at 50% -8%, rgba(255,246,220,0.5) 0%, rgba(255,246,220,0.08) 55%, transparent 75%), radial-gradient(45% 70% at 0% 62%, rgba(214,40,57,0.38) 0%, transparent 70%), radial-gradient(45% 70% at 100% 62%, rgba(31,111,224,0.38) 0%, transparent 70%), linear-gradient(180deg, #0d0d14 0%, #08080c 60%, #040406 100%)",
            }}
          />
          {/* Crowd — scattered camera-flash bokeh behind the cage */}
          <svg
            className="absolute inset-x-0 top-0 h-[62%] w-full"
            aria-hidden="true"
            focusable="false"
          >
            {CROWD_BOKEH.map((dot) => (
              <circle
                key={`bokeh-${dot.x}-${dot.y}`}
                cx={`${dot.x}%`}
                cy={`${dot.y}%`}
                r={dot.r}
                fill={dot.warm ? "#ffe2b4" : "#cfe0ff"}
                opacity={dot.o}
              />
            ))}
          </svg>

          <Octagon id={id} />
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_110%_120%_at_50%_45%,transparent_52%,rgba(0,0,0,0.6)_100%)]" />
      }
    />
  );
}
